import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser, faCog, faShieldAlt, faCheckCircle, faClock,
  faExclamationTriangle, faArrowRight, faBuilding, faHome,
  faKey, faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { useLanguage } from "../../../i18n/LanguageContext";
import { handleSessionExpiry } from "../../../utils/session";
import "./profile.css";

export default function Profile() {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const tr = (key, fallback) => {
    const v = t(key);
    return v === key ? fallback : v;
  };

  const TABS = [
    { id: "personal", label: tr("profile.tabPersonal",    "Personal Info"),    icon: faUser },
    { id: "account",  label: tr("profile.tabAccount",     "Account Settings"), icon: faCog },
    { id: "kyc",      label: tr("profile.tabKyc",         "Identity (KYC)"),   icon: faShieldAlt },
  ];

  const KYC_STATUS_META = {
    not_submitted: { label: tr("profile.kycNotSubmitted", "Not Submitted"),         className: "not_submitted", icon: faExclamationTriangle },
    pending:       { label: tr("profile.kycPending",      "Under Review"),           className: "pending",       icon: faClock },
    approved:      { label: tr("profile.kycApproved",     "Verified"),               className: "approved",      icon: faCheckCircle },
    rejected:      { label: tr("profile.kycRejected",     "Rejected"),               className: "rejected",      icon: faExclamationTriangle },
    resubmit:      { label: tr("profile.kycResubmit",     "Resubmission Required"),  className: "resubmit",      icon: faExclamationTriangle },
  };
  const [tab, setTab] = useState("personal");

  // ── Profile data ───────────────────────────────────────────────────────────
  const [profile, setProfile]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [fetchErr, setFetchErr] = useState("");

  // ── Personal info form ─────────────────────────────────────────────────────
  const [firstName,      setFirstName]      = useState("");
  const [lastName,       setLastName]       = useState("");
  const [countryCode,    setCountryCode]    = useState("");
  const [phone,          setPhone]          = useState("");
  const [gender,         setGender]         = useState("");
  const [calendarPref,   setCalendarPref]   = useState("BS");
  const [piSaving,       setPiSaving]       = useState(false);
  const [piSuccess,      setPiSuccess]      = useState("");
  const [piError,        setPiError]        = useState("");

  // ── Change password form ───────────────────────────────────────────────────
  const [oldPw,      setOldPw]      = useState("");
  const [newPw,      setNewPw]      = useState("");
  const [confirmPw,  setConfirmPw]  = useState("");
  const [pwSaving,   setPwSaving]   = useState(false);
  const [pwSuccess,  setPwSuccess]  = useState("");
  const [pwError,    setPwError]    = useState("");

  // ── Delete account confirmation ────────────────────────────────────────────
  const [deletePhase, setDeletePhase] = useState(0); // 0 = idle, 1 = confirm

  // ── Fetch profile on mount ─────────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { handleSessionExpiry(navigate); return; }

    (async () => {
      try {
        const res = await fetch("/api/user/profile", {
          headers: { Authorization: `Bearer ${token}` },
        });

        // Handle non-JSON responses (e.g. HTML 502 from proxy when backend is down)
        const contentType = res.headers.get("content-type") || "";
        if (!contentType.includes("application/json")) {
          setFetchErr("Cannot reach the server. Make sure the backend is running on port 7000.");
          setLoading(false);
          return;
        }

        const data = await res.json();

        if (res.status === 401 || res.status === 403) { handleSessionExpiry(navigate); return; }
        if (!res.ok) {
          setFetchErr(data.message || "Failed to load profile. Please refresh.");
          setLoading(false);
          return;
        }

        setProfile(data);
        setFirstName(data.firstname         || "");
        setLastName(data.lastname           || "");
        setCountryCode(data.countrycode     || "");
        setPhone(data.phnumber              || "");
        setGender(data.gender               || "");
        setCalendarPref(data.calendar_preference || "BS");
        localStorage.setItem("calendarPreference", data.calendar_preference || "BS");
        setLoading(false);
      } catch (err) {
        setFetchErr(tr("profile.serverError", "Cannot reach the server. Make sure the backend is running on port 7000."));
        setLoading(false);
      }
    })();
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Save personal info ─────────────────────────────────────────────────────
  const savePersonalInfo = async () => {
    setPiError(""); setPiSuccess("");
    if (!firstName.trim() || !lastName.trim()) {
      setPiError(tr("profile.saveErrorRequired", "First name and last name are required."));
      return;
    }
    setPiSaving(true);
    const token = localStorage.getItem("token");
    try {
      const res = await fetch("/api/user/profile", {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ firstname: firstName, lastname: lastName, countrycode: countryCode, phnumber: phone, gender, calendar_preference: calendarPref }),
      });
      const data = await res.json();
      if (res.ok) {
        setPiSuccess(tr("profile.saveSuccess", "Profile updated successfully."));
        // Sync localStorage so header avatar updates on next page load
        localStorage.setItem("firstname", firstName.trim());
        localStorage.setItem("lastname",  lastName.trim());
        localStorage.setItem("calendarPreference", calendarPref);
        setProfile((p) => ({ ...p, firstname: firstName.trim(), lastname: lastName.trim() }));
      } else {
        setPiError(data.message || tr("profile.saveErrorFailed", "Failed to update profile."));
      }
    } catch {
      setPiError(tr("profile.networkError", "Network error. Please try again."));
    } finally {
      setPiSaving(false);
    }
  };

  // ── Change password ────────────────────────────────────────────────────────
  const changePassword = async () => {
    setPwError(""); setPwSuccess("");
    if (!oldPw || !newPw || !confirmPw) { setPwError(tr("profile.pwErrorRequired", "All fields are required.")); return; }
    if (newPw !== confirmPw)            { setPwError(tr("profile.pwErrorMismatch", "New passwords do not match.")); return; }
    if (newPw.length < 8)              { setPwError(tr("profile.pwErrorTooShort", "New password must be at least 8 characters.")); return; }
    setPwSaving(true);
    const token = localStorage.getItem("token");
    try {
      const res = await fetch("/api/auth/change-password", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ oldPassword: oldPw, newPassword: newPw }),
      });
      const data = await res.json();
      if (res.ok) {
        setPwSuccess(tr("profile.pwSuccess", "Password changed successfully."));
        setOldPw(""); setNewPw(""); setConfirmPw("");
      } else {
        setPwError(data.message || tr("profile.pwErrorFailed", "Failed to change password."));
      }
    } catch {
      setPwError(tr("profile.networkError", "Network error. Please try again."));
    } finally {
      setPwSaving(false);
    }
  };

  // ── Derived ────────────────────────────────────────────────────────────────
  const initials = profile
    ? `${(profile.firstname || "").charAt(0)}${(profile.lastname || "").charAt(0)}`.toUpperCase() || "?"
    : "?";
  const kycMeta  = KYC_STATUS_META[profile?.kyc_status] || KYC_STATUS_META.not_submitted;
  const isOwner  = profile?.role === "owner";

  if (loading) {
    return (
      <div className="profile-page" style={{ textAlign: "center", paddingTop: "4rem" }}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  if (fetchErr) {
    return (
      <div className="profile-page" style={{ textAlign: "center", paddingTop: "3rem" }}>
        <p style={{ color: "#dc2626", fontWeight: 600, marginBottom: ".5rem" }}>{fetchErr}</p>
        <button
          className="profile-save-btn"
          onClick={() => window.location.reload()}
          style={{ marginTop: ".5rem" }}
        >
          {tr("profile.retry", "Retry")}
        </button>
      </div>
    );
  }

  return (
    <div className="profile-page">

      {/* ── Avatar card ─────────────────────────────────────────────────────── */}
      <div className="profile-avatar-card">
        <div className="profile-avatar">{initials}</div>
        <div className="profile-avatar-info">
          <h5>{profile.firstname} {profile.lastname}</h5>
          <p>{profile.email}</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: ".3rem", marginTop: ".3rem" }}>
            <span className={`profile-role-badge ${isOwner ? "owner" : "renter"}`}>
              <FontAwesomeIcon icon={isOwner ? faBuilding : faHome} />
              {isOwner ? tr("profile.roleOwner", "Property Owner") : tr("profile.roleRenter", "Renter")}
            </span>
            <span className={`profile-kyc-badge ${kycMeta.className}`}>
              <FontAwesomeIcon icon={kycMeta.icon} />
              {kycMeta.label}
            </span>
          </div>
        </div>
      </div>

      {/* ── Tab strip ───────────────────────────────────────────────────────── */}
      <div className="profile-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            className={`profile-tab-btn${tab === t.id ? " active" : ""}`}
            onClick={() => { setTab(t.id); setPiError(""); setPiSuccess(""); setPwError(""); setPwSuccess(""); }}
          >
            <FontAwesomeIcon icon={t.icon} />
            {t.label}
          </button>
        ))}
      </div>

      {/* ══════════════════════════ PERSONAL INFO TAB ═══════════════════════ */}
      {tab === "personal" && (
        <div className="profile-section-card">
          <h6>{tr("profile.personalTitle", "Personal Information")}</h6>

          <div className="profile-form-row">
            <div className="profile-form-group">
              <label>{tr("profile.firstName", "First Name")}</label>
              <input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder={tr("profile.firstNamePlaceholder", "Enter first name")}
                maxLength={100}
              />
            </div>
            <div className="profile-form-group">
              <label>{tr("profile.lastName", "Last Name")}</label>
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder={tr("profile.lastNamePlaceholder", "Enter last name")}
                maxLength={100}
              />
            </div>

            <div className="profile-form-group">
              <label>{tr("profile.email", "Email Address")}</label>
              <input
                value={profile.email}
                readOnly
                title={tr("profile.email", "Email Address")}
              />
            </div>
            <div className="profile-form-group">
              <label>{tr("profile.gender", "Gender")}</label>
              <select value={gender} onChange={(e) => setGender(e.target.value)}>
                <option value="">{tr("profile.genderSelect", "— Select —")}</option>
                <option value="male">{tr("profile.genderMale", "Male")}</option>
                <option value="female">{tr("profile.genderFemale", "Female")}</option>
                <option value="other">{tr("profile.genderOther", "Other")}</option>
                <option value="prefer_not_to_say">{tr("profile.genderPreferNot", "Prefer not to say")}</option>
              </select>
            </div>

            <div className="profile-form-group">
              <label>{tr("profile.countryCode", "Country Code")}</label>
              <input
                value={countryCode}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "");
                  setCountryCode(v);
                }}
                placeholder={tr("profile.countryCodePlaceholder", "977")}
                maxLength={6}
                inputMode="numeric"
              />
            </div>
            <div className="profile-form-group">
              <label>{tr("profile.phone", "Phone Number")}</label>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={tr("profile.phonePlaceholder", "98XXXXXXXX")}
                maxLength={20}
              />
            </div>

            <div className="profile-form-group">
              <label>{tr("profile.calendarPreference", "Calendar Preference")}</label>
              <select value={calendarPref} onChange={(e) => setCalendarPref(e.target.value)}>
                <option value="BS">{tr("profile.calendarBS", "Bikram Sambat (BS) — Nepali Calendar")}</option>
                <option value="AD">{tr("profile.calendarAD", "Anno Domini (AD) — Gregorian Calendar")}</option>
              </select>
              <small className="profile-field-hint">
                {tr("profile.calendarHint", "Used for rent due dates, payment reminders, and date displays.")}
              </small>
            </div>
          </div>

          <div className="profile-save-row">
            <button
              className="profile-save-btn"
              onClick={savePersonalInfo}
              disabled={piSaving}
            >
              {piSaving ? tr("profile.saving", "Saving…") : tr("profile.saveChanges", "Save Changes")}
            </button>
            {piSuccess && <span className="profile-save-success">{piSuccess}</span>}
            {piError   && <span className="profile-save-error">{piError}</span>}
          </div>
        </div>
      )}

      {/* ══════════════════════════ ACCOUNT SETTINGS TAB ════════════════════ */}
      {tab === "account" && (
        <>
          {/* Change password */}
          <div className="profile-section-card">
            <h6><FontAwesomeIcon icon={faKey} className="me-2" />{tr("profile.changePasswordTitle", "Change Password")}</h6>
            <div className="profile-pw-row">
              <div className="profile-form-group">
                <label>{tr("profile.currentPassword", "Current Password")}</label>
                <input
                  type="password"
                  value={oldPw}
                  onChange={(e) => setOldPw(e.target.value)}
                  placeholder={tr("profile.currentPasswordPlaceholder", "Enter current password")}
                  autoComplete="current-password"
                />
              </div>
              <div className="profile-form-group">
                <label>{tr("profile.newPassword", "New Password")}</label>
                <input
                  type="password"
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  placeholder={tr("profile.newPasswordPlaceholder", "At least 8 characters")}
                  autoComplete="new-password"
                />
              </div>
              <div className="profile-form-group">
                <label>{tr("profile.confirmPassword", "Confirm New Password")}</label>
                <input
                  type="password"
                  value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)}
                  placeholder={tr("profile.confirmPasswordPlaceholder", "Repeat new password")}
                  autoComplete="new-password"
                />
              </div>
            </div>
            <div className="profile-save-row">
              <button
                className="profile-save-btn"
                onClick={changePassword}
                disabled={pwSaving}
              >
                {pwSaving ? tr("profile.saving", "Saving…") : tr("profile.updatePassword", "Update Password")}
              </button>
              {pwSuccess && <span className="profile-save-success">{pwSuccess}</span>}
              {pwError   && <span className="profile-save-error">{pwError}</span>}
            </div>
          </div>

          {/* Account info — read-only */}
          <div className="profile-section-card">
            <h6>{tr("profile.accountDetailsTitle", "Account Details")}</h6>
            <div className="profile-form-row">
              <div className="profile-form-group">
                <label>{tr("profile.accountType", "Account Type")}</label>
                <input value={isOwner ? tr("profile.roleOwner", "Property Owner") : tr("profile.roleRenter", "Renter")} readOnly />
              </div>
              <div className="profile-form-group">
                <label>{tr("profile.email", "Email Address")}</label>
                <input value={profile.email} readOnly />
              </div>
            </div>
          </div>

          {/* Danger zone */}
          <div className="profile-section-card profile-danger-zone">
            <h6><FontAwesomeIcon icon={faTrash} className="me-2" />{tr("profile.dangerZoneTitle", "Danger Zone")}</h6>
            <p className="profile-danger-text">
              {tr("profile.dangerZoneText", "Deleting your account is permanent. All your data — listings, applications, messages, and KYC records — will be removed. This action cannot be undone.")}
            </p>
            {deletePhase === 0 && (
              <button
                className="profile-delete-btn"
                onClick={() => setDeletePhase(1)}
              >
                {tr("profile.deleteAccount", "Delete My Account")}
              </button>
            )}
            {deletePhase === 1 && (
              <div style={{ display: "flex", alignItems: "center", gap: ".75rem", flexWrap: "wrap" }}>
                <span style={{ fontSize: ".875rem", color: "#dc2626", fontWeight: 600 }}>
                  {tr("profile.deleteConfirm", "Are you absolutely sure? This cannot be undone.")}
                </span>
                <button
                  className="profile-delete-btn"
                  onClick={() => {
                    /* Contact support to delete — intentionally no self-serve delete API */
                    navigate("/contact");
                  }}
                >
                  {tr("profile.deleteContactSupport", "Yes, contact support to delete")}
                </button>
                <button
                  className="profile-save-btn"
                  style={{ background: "#6b7280" }}
                  onClick={() => setDeletePhase(0)}
                >
                  {tr("profile.cancel", "Cancel")}
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* ══════════════════════════ KYC TAB ═════════════════════════════════ */}
      {tab === "kyc" && (
        <div className="profile-section-card">
          <h6><FontAwesomeIcon icon={faShieldAlt} className="me-2" />{tr("profile.kycTitle", "Identity Verification (KYC)")}</h6>

          <div className="profile-kyc-info-row">
            <span className="profile-kyc-info-label">{tr("profile.kycStatus", "Status")}</span>
            <span className={`profile-kyc-badge ${kycMeta.className}`} style={{ margin: 0 }}>
              <FontAwesomeIcon icon={kycMeta.icon} />
              {kycMeta.label}
            </span>
          </div>

          {profile.kyc_verified_at && (
            <div className="profile-kyc-info-row">
              <span className="profile-kyc-info-label">{tr("profile.kycVerifiedOn", "Verified On")}</span>
              <span className="profile-kyc-info-value">
                {new Date(profile.kyc_verified_at).toLocaleDateString("en-NP", { year: "numeric", month: "long", day: "numeric" })}
              </span>
            </div>
          )}

          <div className="profile-kyc-info-row">
            <span className="profile-kyc-info-label">{tr("profile.kycAccountRole", "Account Role")}</span>
            <span className="profile-kyc-info-value">{isOwner ? tr("profile.roleOwner", "Property Owner") : tr("profile.roleRenter", "Renter")}</span>
          </div>

          {/* What KYC unlocks for this role */}
          <div style={{ marginTop: "1.1rem", background: "#f9fafb", borderRadius: 10, padding: "1rem" }}>
            <p style={{ fontSize: ".8rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: ".6rem" }}>
              {profile.kyc_status === "approved"
                ? tr("profile.kycUnlockedLabel", "What you unlocked")
                : tr("profile.kycGetVerifiedLabel", "What you get when verified")}
            </p>
            <ul style={{ margin: 0, paddingLeft: "1.1rem", fontSize: ".875rem", color: "#374151", lineHeight: 1.9 }}>
              {(() => {
                const benefits = isOwner
                  ? t("profile.kycOwnerBenefits")
                  : t("profile.kycRenterBenefits");
                const fallback = isOwner
                  ? ["Verified badge on every listing", "Higher search ranking over unverified listings", "3× more rental inquiries from renters", "Ability to receive digital rent payments", "Legal identity protection in disputes"]
                  : ["KYC badge on your renter profile", "2× faster approval from property owners", "Access to premium verified property listings", "Owners respond faster to verified renters", "Identity protection in rental disputes"];
                const list = Array.isArray(benefits) ? benefits : fallback;
                return list.map((item, i) => <li key={i}>{item}</li>);
              })()}
            </ul>
          </div>

          {/* CTA */}
          {profile.kyc_status !== "approved" && (
            <Link
              to="/kyc-verification"
              className={`profile-kyc-cta${profile.kyc_status === "rejected" || profile.kyc_status === "resubmit" ? " urgent" : ""}`}
            >
              <FontAwesomeIcon icon={faShieldAlt} />
              {profile.kyc_status === "pending"
                ? tr("profile.kycCtaPending", "View Submission Status")
                : profile.kyc_status === "rejected" || profile.kyc_status === "resubmit"
                ? tr("profile.kycCtaResubmit", "Fix & Resubmit Documents")
                : tr("profile.kycCtaStart", "Start Verification — 5 min")}
              <FontAwesomeIcon icon={faArrowRight} />
            </Link>
          )}
        </div>
      )}

    </div>
  );
}
